import React, { useState, useEffect } from 'react';
import { canEdit, isAdminUser } from '../utils/permissions';
import { asService } from '../services/asService';
import { customerService } from '../services/customerService';
import { engineerService } from '../services/engineerService';
import { vehicleService } from '../services/vehicleService'; 
import { partService } from '../services/partService'; 
import { pdf } from '@react-pdf/renderer';

import AsInvoicePdf from '../components/as/AsInvoicePdf';
import PartSalesInvoicePdf from '../components/as/PartSalesInvoicePdf'; 
import AsReportPdf from '../components/as/AsReportPdf'; 
import { printReport } from '../utils/printUtils';

import AsReserve from '../components/as/AsReserve'; 
import AsRegister from '../components/as/AsRegister'; 
import AsSettle from '../components/as/AsSettle'; 
import AsList from '../components/as/AsList'; 

const getLocalDateString = (dateObj = new Date()) => {
  const year = dateObj.getFullYear();
  const month = String(dateObj.getMonth() + 1).padStart(2, '0');
  const day = String(dateObj.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const getWeekRange = (dateStr) => {
  const [y, m, d] = dateStr.split('-');
  const date = new Date(y, m - 1, d);
  const day = date.getDay();
  const diffToMonday = date.getDate() - day + (day === 0 ? -6 : 1);
  const monday = new Date(y, m - 1, diffToMonday);
  const sunday = new Date(y, m - 1, diffToMonday + 6);
  return { start: getLocalDateString(monday), end: getLocalDateString(sunday) };
};

const As = () => {
  const styles = {
    wrapper: { padding: '50px 80px', background: '#f4f7f9', minHeight: '100vh' },
    input: { width: '100%', padding: '14px 18px', border: '1px solid #e2e8f0', borderRadius: '12px', fontSize: '14px', boxSizing: 'border-box', outline: 'none' },
    btnMain: { padding: '16px 32px', background: '#1e293b', color: 'white', border: 'none', borderRadius: '12px', fontWeight: 'bold', cursor: 'pointer' },
    btnGhost: { padding: '10px 20px', background: 'white', border: '1px solid #e2e8f0', borderRadius: '10px', cursor: 'pointer', color: '#64748b' },
    actionPrintBtn: { padding: '10px 18px', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' },
    actionExcelBtn: { padding: '10px 18px', background: '#10b981', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' },
    actionBatchBtn: { padding: '10px 18px', background: '#ef4444', color: 'white', border: 'none', borderRadius: '10px', fontWeight: 'bold', cursor: 'pointer', fontSize: '13px' },
    btnFilter: (isActive) => ({
      padding: '8px 16px', fontSize: '13px', borderRadius: '10px', cursor: 'pointer', fontWeight: 'bold',
      border: isActive ? 'none' : '1px solid transparent', background: isActive ? '#3b82f6' : 'transparent', color: isActive ? 'white' : '#64748b', transition: 'all 0.2s'
    })
  };

  const [records, setRecords] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [engineers, setEngineers] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [parts, setParts] = useState([]); 
  const [viewMode, setViewMode] = useState('LIST');
  const [activeRecord, setActiveRecord] = useState(null);
  const [repairParts, setRepairParts] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [settleDate, setSettleDate] = useState(getLocalDateString());
  const [settleStatus, setSettleStatus] = useState('완료');
  const [discount, setDiscount] = useState(0); // 정산 할인액(부가세 면제 등)
  const [isEditing, setIsEditing] = useState(false); 
  const [filterPeriod, setFilterPeriod] = useState('전체'); 
  const [filterDate, setFilterDate] = useState(getLocalDateString());
  const [isReserveModalOpen, setIsReserveModalOpen] = useState(false);

  const initialPart = { taskName: '', type: 'A', qty: 1, price: 0, labor: 0, note: '일반수리' };
  const [newPart, setNewPart] = useState(initialPart);
  
  const initialForm = { id: null, serviceCategory: '정비', customerName: '', carNumber: '', vin: '', mileage: '', mechanic: '', asDate: getLocalDateString(), symptom: '', inspection: '', cause: '', action: '', result: '', repairType: '유상', status: '접수' };
  const [formData, setFormData] = useState(initialForm);

  const refreshData = async () => {
    try {
      const [r, c, en, v, p] = await Promise.all([
        asService.getRecords(), customerService.getCustomers(), engineerService.getEngineers(), vehicleService.getVehicles(), partService.getParts()
      ]);
      setRecords(r || []); setCustomers(c || []); setEngineers(en || []); setVehicles(v || []); setParts(p || []);
    } catch (error) { console.error("데이터 로딩 오류:", error); }
  };

  useEffect(() => { refreshData(); }, []);

  const handleRegister = async (e) => {
    e.preventDefault();
    const isCustomerExist = customers.some(c => c.name === formData.customerName);
    if (!isCustomerExist) {
      alert('⚠️ 유효하지 않은 고객입니다.\n[고객 관리] 메뉴에서 먼저 고객 정보를 등록해야 접수가 가능합니다.');
      return;
    }

    try {
      if (isEditing) {
        await asService.updateRecord(formData);
        alert('✅ 내역이 수정되었습니다.');
      } else {
        await asService.saveRegistration(formData);
        alert('✅ 접수가 완료되었습니다.');
      }
      setFormData(initialForm);
      setIsEditing(false);
      setViewMode('LIST');
      refreshData();
    } catch (error) {
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  const handleEditClick = (record) => {
    setFormData(record);
    setIsEditing(true);
    setActiveRecord(record); 
    setRepairParts(record.parts || []);
    setDiscount(Number(record.discount) || 0); // 정산 화면 재진입 시 직전 건 할인액이 남지 않도록
    setSettleDate(record.settleDate || record.asDate || getLocalDateString());
    setSettleStatus(record.status === '접수' ? '완료' : record.status); 
    setViewMode('REG');
    window.scrollTo(0, 0);
  };

  const handleDelete = async (id) => {
    if (window.confirm('정말 이 내역을 삭제하시겠습니까? 삭제된 데이터는 복구할 수 없습니다.')) {
      try {
        await asService.remove(id);
        alert('삭제가 완료되었습니다.');
        refreshData();
      } catch (error) {
        alert('삭제 중 오류가 발생했습니다.');
      }
    }
  };

  const handleDownloadPDFFromRow = async (record) => {
    try {
      const customer = customers.find(c => c.name === record.customerName) || {};
      const vehicle = vehicles.find(v => v.carNumber === record.carNumber) || {};
      const totalWithVat = Number(record.totalCost || 0); 
      const subTotal = Math.round(totalWithVat / 1.1); 
      const vat = totalWithVat - subTotal; 
      
      let blob;
      let fileName;

      if (record.serviceCategory === '부품판매') {
        const invoiceData = {
          customerName: record.customerName || '', 
          phone: customer.phone || '', 
          address: customer.address || '',
          completeDate: record.settleDate || record.asDate, 
          items: record.parts && record.parts.length > 0 ? record.parts : [],
        };
        blob = await pdf(<PartSalesInvoicePdf invoiceData={invoiceData} />).toBlob();
        fileName = `물품거래명세서_${record.customerName}.pdf`;
      } 
      else {
        const invoiceData = {
          carNumber: record.carNumber || '', 
          customerName: record.customerName || '', 
          modelName: vehicle.modelName || '', 
          regDate: vehicle.customsDate || '정보 없음',
          mileage: `${Number(record.mileage || 0).toLocaleString()}km`, 
          vin: record.vin || vehicle.vin || '정보 없음',
          phone: customer.phone || '정보 없음', 
          address: customer.address || '주소 정보 없음',
          completeDate: record.settleDate || record.asDate, 
          requestDate: record.asDate, 
          engineer: record.mechanic || '',
          items: record.parts && record.parts.length > 0 ? record.parts : [{ taskName: record.result || '정비 작업', type: 'A', qty: 1, price: subTotal, labor: 0, note: '일반수리' }],
          discount: Number(record.discount) || 0,
          subTotal, vat, total: totalWithVat
        };
        blob = await pdf(<AsInvoicePdf invoiceData={invoiceData} />).toBlob();
        fileName = `정비명세서_${record.carNumber}.pdf`;
      }

      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = fileName;
      document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
    } catch (error) { alert("PDF 생성 오류가 발생했습니다."); console.error(error); }
  };

  const handleDownloadRepairReport = async (record) => {
    if (record.serviceCategory === '부품판매') {
      alert("부품 판매 건은 정비내역서가 존재하지 않습니다.");
      return;
    }
    try {
      const vehicle = vehicles.find(v => v.carNumber === record.carNumber) || {};
      const dynamicModelName = vehicle.modelName || '차량';
      const reportData = {
        modelName: dynamicModelName, vin: record.vin || vehicle.vin || '정보 없음', carNumber: record.carNumber || '',
        asDate: record.asDate || '', mechanic: record.mechanic || '', mileage: record.mileage || '0', repairType: record.repairType || '일반',
        symptom: record.symptom || '', inspection: record.inspection || '', cause: record.cause || '', action: record.action || '', result: record.result || ''
      };
      
      const blob = await pdf(<AsReportPdf invoiceData={reportData} />).toBlob();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url; link.download = `정비내역서_${record.carNumber}.pdf`;
      document.body.appendChild(link); link.click(); document.body.removeChild(link); URL.revokeObjectURL(url);
    } catch (error) { alert("정비내역서 PDF 생성 오류"); }
  };

  const handleSettleClick = (record) => {
    setActiveRecord(record); setRepairParts(record.parts || []);
    setSettleDate(record.settleDate || record.asDate || getLocalDateString());
    setSettleStatus(record.status === '접수' ? '완료' : record.status);
    setDiscount(Number(record.discount) || 0); // 기존 할인액 불러오기(없으면 0)
    setViewMode('SETTLE'); window.scrollTo(0, 0);
  };

  const handleSettleSave = async () => {
    const supplyValue = repairParts.reduce((sum, p) => sum + (Number(p.price || 0) * Number(p.qty || 0)) + Number(p.labor || 0), 0);
    const grossWithVat = Math.round(supplyValue * 1.1);        // 할인 전 (공급가 + 부가세)
    const discountAmount = Number(discount) || 0;               // 할인액
    const totalWithVat = grossWithVat - discountAmount;         // 최종 결제 금액 (할인 반영)
    const updated = { ...activeRecord, status: settleStatus, settleDate, parts: repairParts, discount: discountAmount, totalCost: totalWithVat };
    
    try {
      // 재고는 '이미 빼간 양'과의 차이만 반영하므로, 재정산이어도 그대로 다시 부르면 된다.
      const result = await asService.settleRecordWithParts(updated, repairParts, settleDate, parts);

      await refreshData(); 
      setViewMode('LIST'); 
      setIsEditing(false);

      // 조용히 넘어가면 재고가 어긋난 걸 모르므로 반드시 알린다.
      const warn = [];
      if (result && result.unmatched && result.unmatched.length) {
        warn.push(`· 부품 마스터에서 찾지 못해 재고에 반영되지 않은 품목: ${result.unmatched.join(', ')}`);
      }
      if (result && result.negatives && result.negatives.length) {
        warn.push(`· 재고가 모자라 마이너스가 된 부품: ${result.negatives.join(', ')}`);
      }
      alert('✅ 정산 데이터가 성공적으로 반영되었습니다.' + (warn.length ? `

⚠️ 확인이 필요합니다
${warn.join('\n')}` : ''));

    } catch (error) {
      console.error("정산 및 부품 연동 에러:", error);
      alert('저장 중 오류가 발생했습니다.');
    }
  };

  // 과거 데이터 재고 일괄 반영 — 마스터(관리자)만 사용한다.
  // 각 건에 '빼간 양'(stockApplied)이 기록되므로, 다시 눌러도 중복 차감되지 않는다.
  const handleBatchStockReflection = async () => {
    const targetRecords = records.filter(r =>
      r.status === '완료' && r.parts && r.parts.length > 0
      && !r.stockApplied && !r.isStockReflected   // 아직 한 번도 반영되지 않은 건만
    );

    if (targetRecords.length === 0) {
      alert('재고에 반영되지 않은 완료 건이 없습니다.');
      return;
    }

    const ok = window.confirm(
      `재고에 아직 반영되지 않은 완료 건 ${targetRecords.length}건을 반영합니다.

` +
      `· 이미 반영된 건은 자동으로 제외됩니다
` +
      `· 각 건에 '빼간 양'이 기록되어 다시 눌러도 중복 차감되지 않습니다

진행할까요?`
    );
    if (!ok) return;

    try {
      let done = 0;
      const allUnmatched = [];
      for (const record of targetRecords) {
        const res = await asService.settleRecordWithParts(
          record, record.parts, record.settleDate || record.asDate || getLocalDateString(), parts
        );
        if (res && res.unmatched && res.unmatched.length) allUnmatched.push(...res.unmatched);
        done++;
      }
      await refreshData();
      const uniq = Array.from(new Set(allUnmatched));
      alert(`🎉 ${done}건을 재고에 반영했습니다.` +
        (uniq.length ? `

⚠️ 부품 마스터에서 찾지 못해 반영되지 않은 품목: ${uniq.join(', ')}` : ''));
    } catch (error) {
      console.error("일괄 반영 중 에러:", error);
      alert('일괄 처리 중 오류가 발생했습니다.\n이미 처리된 건은 기록되어 있어 다시 눌러도 중복되지 않습니다.');
    }
  };

  const handleDownloadAsExcel = () => {
    const headers = ['No', '상태', '유형', '접수일', '정산일', '고객명', '차량번호', '담당자', '총합계'];
    const csvRows = [headers.join(',')];
    filteredRecords.forEach((r, idx) => {
      csvRows.push([
        filteredRecords.length - idx,
        r.status || '접수',
        r.serviceCategory || '정비',
        r.asDate || '',
        r.settleDate || '',
        r.customerName || '',
        r.carNumber || '',
        r.mechanic || '',
        r.totalCost || 0
      ].join(','));
    });
    const csvString = '\uFEFF' + csvRows.join('\n'); 
    const blob = new Blob([csvString], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `AS현황_${getLocalDateString()}.csv`;
    link.click();
  };

  const handlePrintAsReport = () => {
    if (typeof printReport === 'function') {
      const title = 'AS 처리 현황 목록';
      const headers = ['상태', '유형', '접수일', '정산일', '고객명', '차량번호', '담당자', '총합계'];
      const rows = filteredRecords.map(r => [
        r.status || '접수',
        r.serviceCategory || '정비',
        r.asDate || '-',
        r.settleDate || '-',
        r.customerName || '-',
        r.carNumber || '-',
        r.mechanic || '-',
        `₩ ${Number(r.totalCost || 0).toLocaleString()}`
      ]);
      const metaInfo = { '검색어': searchTerm || '전체', '필터 기준': filterPeriod };
      printReport(title, headers, rows, metaInfo);
    } else {
      window.print();
    }
  };

  const safeRecords = records || [];
  
  // 🚀 [핵심 버그 수정] 상태별 스마트 그룹핑 및 방향 맞춤형 정렬 엔진
  const filteredRecords = safeRecords.filter(r => {
    const matchSearch = 
      (r.customerName || '').toLowerCase().includes(searchTerm.toLowerCase()) || 
      (r.carNumber || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
      (r.parts && Array.isArray(r.parts) && r.parts.some(p => (p.taskName || '').toLowerCase().includes(searchTerm.toLowerCase())));
      
    if (!matchSearch) return false;
    if (filterPeriod === '전체') return true;
    
    const d = r.settleDate || r.asDate || ''; 
    if (!d) return false;
    if (filterPeriod === '일일') return d === filterDate;
    if (filterPeriod === '주간') { const range = getWeekRange(filterDate); return d >= range.start && d <= range.end; }
    if (filterPeriod === '월간') return d.startsWith(filterDate.substring(0, 7));
    return true;
  }).sort((a, b) => {
    // 1. 상태별 가중치 부여 (화면 출력 순서 제어용)
    const getStatusWeight = (status) => {
      if (status === '예약') return 1; // 가장 상단 그룹
      if (status === '완료') return 3; // 가장 하단 그룹
      return 2;                        // 중간 그룹 (접수, 진행중, 보류 등)
    };

    const weightA = getStatusWeight(a.status);
    const weightB = getStatusWeight(b.status);

    // 가중치가 다르면 그룹별로 층을 나눔 (1층: 예약 -> 2층: 미완료 -> 3층: 완료)
    if (weightA !== weightB) {
      return weightA - weightB;
    }

    // 2. 같은 층(그룹) 내에서의 날짜 정렬 방향 설정
    if (weightA === 1) {
      // 🚀 [오로지 예약만!] 오름차순: 예약일이 가까운(빠른) 순서대로 정렬
      const dateA = new Date(a.asDate || '9999-12-31');
      const dateB = new Date(b.asDate || '9999-12-31');
      return dateA - dateB;
    } else if (weightA === 3) {
      // 🚀 [완료 건] 기존 유지(내림차순): 가장 최근 정산(완료)된 건이 위로 오게 정렬
      const dateA = new Date(a.settleDate || a.asDate || 0);
      const dateB = new Date(b.settleDate || b.asDate || 0);
      return dateB - dateA;
    } else {
      // 🚀 [일반 미완료 건 - 접수, 진행중, 보류] 내림차순: 과거(5월) 건은 아래로, 오늘(7월) 접수 건은 위로 오게 교정
      const dateA = new Date(a.asDate || 0);
      const dateB = new Date(b.asDate || 0);
      return dateB - dateA;
    }
  });

  return (
    <div style={styles.wrapper}>
      {/* 상단 컨트롤 헤더 영역 (필터/버튼 등) */}
      <div style={{ marginBottom: '40px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', gap: '15px' }}>
          <button onClick={() => { setViewMode('LIST'); setIsEditing(false); }} style={styles.btnGhost}>전체 목록</button>
          {canEdit() && <button onClick={() => { setFormData(initialForm); setIsEditing(false); setViewMode('REG'); }} style={{...styles.btnMain, background:'#3b82f6'}}>+ 신규 접수 (정비/판매)</button>}
          <button onClick={() => setIsReserveModalOpen(true)} style={{...styles.btnGhost, color: '#9333ea', borderColor: '#d8b4fe', background: '#faf5ff', fontWeight: 'bold'}}>🗓️ 예약 및 작업 캘린더</button>
        </div>
        
        {viewMode === 'LIST' && (
          <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
            {isAdminUser() && <button style={styles.actionBatchBtn} onClick={handleBatchStockReflection}>🚀 과거 데이터 재고 일괄 반영</button>}
            <button style={styles.actionPrintBtn} onClick={handlePrintAsReport}>🖨️ 출력하기</button>
            <button style={styles.actionExcelBtn} onClick={handleDownloadAsExcel}>📊 EXCEL 다운로드</button>

            <div style={{display:'flex', alignItems:'center', background:'#fff', padding:'5px', borderRadius:'12px', border:'1px solid #e2e8f0'}}>
              <button type="button" style={styles.btnFilter(filterPeriod === '전체')} onClick={() => setFilterPeriod('전체')}>전체</button>
              <button type="button" style={styles.btnFilter(filterPeriod === '일일')} onClick={() => setFilterPeriod('일일')}>일일</button>
              <button type="button" style={styles.btnFilter(filterPeriod === '주간')} onClick={() => setFilterPeriod('주간')}>주간</button>
              <button type="button" style={styles.btnFilter(filterPeriod === '월간')} onClick={() => setFilterPeriod('월간')}>월간</button>
              
              {filterPeriod !== '전체' && (
                <input 
                  type={filterPeriod === '월간' ? "month" : "date"} 
                  style={{...styles.input, width: '135px', padding: '8px 12px', marginLeft: '5px', border: 'none', background: '#f8fafc'}} 
                  value={filterPeriod === '월간' ? filterDate.substring(0, 7) : filterDate} 
                  onChange={(e) => setFilterDate(e.target.value.length === 7 ? `${e.target.value}-01` : e.target.value)} 
                />
              )}
            </div>
            <input style={{...styles.input, width:'250px'}} placeholder="고객명, 차량 또는 부품명 검색..." value={searchTerm} onChange={e=>setSearchTerm(e.target.value)} />
          </div>
        )}
      </div>

      {viewMode === 'REG' && (
        <AsRegister 
          formData={formData} setFormData={setFormData}
          customers={customers} vehicles={vehicles} engineers={engineers}
          isEditing={isEditing} handleRegister={handleRegister}
          setViewMode={setViewMode} setIsEditing={setIsEditing}
        />
      )}

      {viewMode === 'SETTLE' && (
        <AsSettle
          activeRecord={activeRecord} settleDate={settleDate} setSettleDate={setSettleDate}
          newPart={newPart} setNewPart={setNewPart} initialPart={initialPart}
          repairParts={repairParts} setRepairParts={setRepairParts}
          discount={discount} setDiscount={setDiscount}
          handleSettleSave={handleSettleSave} setViewMode={setViewMode} setIsEditing={setIsEditing}
        />
      )}

      {viewMode === 'LIST' && (
        <AsList 
          filteredRecords={filteredRecords}
          handleEditClick={handleEditClick}
          handleDownloadRepairReport={handleDownloadRepairReport}
          handleDownloadPDFFromRow={handleDownloadPDFFromRow}
          handleSettleClick={handleSettleClick}
          handleDelete={handleDelete}
        />
      )}

      <AsReserve isOpen={isReserveModalOpen} onClose={() => setIsReserveModalOpen(false)} records={records} />
    </div>
  );
};

export default As;